import { PdfGen } from '../../pdf/pdf';
import { ILayoutLabelGen } from '../layoutLabel';
import { OptsLabel } from '../optsLabel';
import { GUtil } from '../../pdf/gutil';
import { GLabelType } from '../../pdf/glabel';
import {
  ELogicoVariant,
  ELogicoMode,
  LogicoData,
  drawLogico,
  genMap,
  logicoCount,
} from '../../pdf/board/logico';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/helpers';
import '../../pdf/draw/label';
import { IItemCount } from '@educraft/base';

/**
 * Base class for Logico puzzle generators (Label version).
 *
 * Port of C# EduFrame.Single.Generators.LogicoBase and Img10.Generators.PiccoloBase.
 * Handles both text (Single) and image (Img10) content via GLabel.
 */
abstract class LogicoBase implements ILayoutLabelGen, IItemCount {
  constructor(
    protected readonly variant: ELogicoVariant,
    protected readonly mode: ELogicoMode
  ) {}

  countDefault(): number {
    return logicoCount(this.variant);
  }
  countMin(): number {
    return logicoCount(this.variant);
  }
  countMax(): number {
    return logicoCount(this.variant);
  }

  public async generate(pdf: PdfGen, opts: OptsLabel): Promise<void> {
    const count = logicoCount(this.variant);
    pdf.addPage();
    pdf.addPage();

    const items = opts.list.slice(0, count);
    const map = genMap(items.length, opts.data.random);

    // Track font, adjusted once for all text items to ensure visual consistency
    let font = GUtil.baseFont;

    drawLogico(
      pdf,
      this.variant,
      this.mode,
      items, // GTuple[] passed directly — left = question GLabel, right = answer GLabel
      map,
      {
        drawQuestion: (data: LogicoData) => {
          if (data.index < 0 || data.index >= items.length) return;
          const item = items[data.invMap[data.index]];
          data.page.drawLabel(item.left, { rect: data.rect, font });
        },
        calcQuestionSize: (size) => {
          // Scale font down so all question texts fit within cell — text labels only
          const texts = items.map((i) => i.left.getText()).filter(Boolean);
          if (texts.length > 0) {
            font = pdf.getPage(0).adjustFontMulti(
              texts.map((t) => [t]),
              GUtil.baseFont,
              size
            );
          }
        },
        data: opts.data,
      }
    );
  }
}

// ===== Piccolo variants =====

export class Piccolo__Base extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Rows);
  }
}

export class Piccolo__Mini extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Mini);
  }
}

export class Piccolo__2Cols extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.TwoColumns);
  }
}

export class Piccolo__ZigZag1 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.ZigZag1);
  }
}

export class Piccolo__ZigZag2 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.ZigZag2);
  }
}

export class Piccolo__Diagonal extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Diagonal);
  }
}

export class Piccolo__Okolo extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Around);
  }
}

export class Piccolo__23 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Mode23);
  }
}

export class Piccolo__343 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Mode343);
  }
}

export class Piccolo__Pyramid extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Pyramid);
  }
}

export class Piccolo__P0 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Path0);
  }
}

export class Piccolo__P1 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Piccolo, ELogicoMode.Path1);
  }
}

// ===== Primo variants =====

export class Primo__Base extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Rows);
  }
}

export class Primo__Mini extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Mini);
  }
}

export class Primo__2Cols extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.TwoColumns);
  }
}

export class Primo__ZigZag1 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.ZigZag1);
  }
}

export class Primo__ZigZag2 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.ZigZag2);
  }
}

export class Primo__Diagonal extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Diagonal);
  }
}

export class Primo__P0 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Path0);
  }
}

export class Primo__P1 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Primo, ELogicoMode.Path1);
  }
}

// ===== Maximo variants =====

export class Maximo__Base extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Rows);
  }
}

export class Maximo__Mini extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Mini);
  }
}

export class Maximo__2Cols extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.TwoColumns);
  }
}

export class Maximo__ZigZag1 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.ZigZag1);
  }
}

export class Maximo__ZigZag2 extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.ZigZag2);
  }
}

export class Maximo__Diagonal extends LogicoBase {
  constructor() {
    super(ELogicoVariant.Maximo, ELogicoMode.Diagonal);
  }
}
